/**
 * ログ API サービス
 *
 * Cloud Functions のログ API を呼び出すためのサービスです。
 */

import { getAuthInstance, getStorageInstance, getFunctionsInstance } from "../lib/firebase";
import { ref, getDownloadURL } from "firebase/storage";
import { httpsCallable } from "firebase/functions";
import { SerializedTimestamp } from "../types/common";
export type { SerializedTimestamp };
import { ProcessStatus } from "../types/status";
import { API_BASE_URL } from "../constants/api";

/**
 * ログ一覧のデフォルトページサイズ
 */
export const DEFAULT_PAGE_SIZE = 20;

/**
 * 添付ファイル情報
 */
export interface Attachment {
  filename: string;
  storagePath: string;
  mimeType: string;
  publicUrl: string;
  ocrDocId?: string;
}

/**
 * メールログ情報
 */
export interface EmailLog {
  fileResults?: Array<{ jobId: string; logId: string; filename: string; state: string; error?: string }>;
  id: string;
  customerId: string;
  customerName?: string;
  eventTypeName?: string;
  from: string;
  to?: string; // 送信先アドレス（顧客特定に使用）
  subject: string;
  receivedAt: SerializedTimestamp;
  status: ProcessStatus;
  attachments: Attachment[];
  ocrResult?: {
    text: string;
    structuredData?: Record<string, unknown>;
    totalTokens?: number;
  };
  errorMessage?: string;
  rejectionReason?: string;
  csvOutput?: {
    storagePath: string;
    filename: string;
    rowCount: number;
    encoding: string;
    generatedAt: SerializedTimestamp;
    columns?: Array<Record<string, unknown>>;
    validationErrors?: string[];
  };
  emailResponse?: {
    status: string;
    to: string[];
    subject: string;
  };
}

/**
 * 処理プロセスの個別ステップ
 */
export interface ProcessStep {
  id: string;
  type: string;
  name: string;
  status: ProcessStatus;
  description?: string;
  details?: Record<string, unknown>;
  timestamp: SerializedTimestamp;
}

/**
 * 認証ヘッダーを取得
 */
async function getAuthHeaders(): Promise<HeadersInit> {
  const auth = getAuthInstance();
  const user = auth.currentUser;

  if (!user) {
    throw new Error("認証されていません");
  }

  const token = await user.getIdToken();

  return {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };
}

/**
 * API エラーをハンドリング
 */
async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: "不明なエラー" }));
    throw new Error(error.error || `HTTP ${response.status}`);
  }
  return response.json();
}

/**
 * ログ一覧を取得（ページング対応）
 */
export async function fetchLogs(options?: {
  status?: string;
  customerId?: string;
  startDate?: Date | null;
  endDate?: Date | null;
  problematicOnly?: boolean;
  page?: number;
  pageSize?: number;
}): Promise<{ logs: EmailLog[]; total: number; page: number; pageSize: number }> {
  const headers = await getAuthHeaders();
  const params = new URLSearchParams();
  if (options?.status) params.append("status", options.status);
  if (options?.customerId) params.append("customerId", options.customerId);
  if (options?.startDate) params.append("startDate", options.startDate.toISOString());
  if (options?.endDate) params.append("endDate", options.endDate.toISOString());
  if (options?.problematicOnly) params.append("problematicOnly", "true");
  if (options?.page) params.append("page", options.page.toString());
  if (options?.pageSize) params.append("pageSize", options.pageSize.toString());

  const url = params.toString() ? `${API_BASE_URL}?${params}` : API_BASE_URL;
  const response = await fetch(url, { headers });
  const data = await handleResponse<{ 
    logs: EmailLog[]; 
    total: number; 
    page: number; 
    pageSize: number 
  }>(response);
  
  return data;
}

/**
 * 特定のログの処理ステップ一覧を取得
 */
export async function fetchProcessSteps(logId: string): Promise<ProcessStep[]> {
  const headers = await getAuthHeaders();
  const url = `${API_BASE_URL}/${logId}/steps`;
  
  try {
    const response = await fetch(url, { headers });
    const data = await handleResponse<{ steps: ProcessStep[] }>(response);
    return data.steps;
  } catch (err: unknown) {
    // API がまだ未実装な場合のフォールバックとして空配列を返す
    console.warn("fetchProcessSteps failed:", err);
    return [];
  }
}

/**
 * Storage からファイルをダウンロード
 */
export async function downloadFile(storagePath: string, filename: string): Promise<void> {
  try {
    const storage = getStorageInstance();
    const fileRef = ref(storage, storagePath);
    const url = await getDownloadURL(fileRef);

    // ブラウザでダウンロードを実行
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } catch (err: unknown) {
    console.error("Download failed:", err);
    throw new Error("ファイルのダウンロードに失敗しました");
  }
}

/**
 * Storage からファイルのダウンロードURLを取得（プレビュー用）
 */
export async function getFileUrl(storagePath: string): Promise<string> {
  const storage = getStorageInstance();
  const fileRef = ref(storage, storagePath);
  return await getDownloadURL(fileRef);
}

/**
 * ログの再処理を実行
 */
export interface ReprocessOptions { attachmentIndexes?: number[]; publish?: boolean }
const pendingOperations = new Map<string, string>();
export async function reprocessLog(logId: string, options: ReprocessOptions = {}): Promise<{ success: boolean; message: string; newLogId?: string }> {
  const functions = getFunctionsInstance();
  const reprocessLogFn = httpsCallable<{ logId: string; operationId: string } & ReprocessOptions, { success: boolean; message: string; newLogId?: string }>(
    functions, 
    "reprocessLog",
    { timeout: 540_000 }
  );
  
  try {
    const signature = JSON.stringify({ logId, ...options });
    const operationId = pendingOperations.get(signature) ?? crypto.randomUUID();
    pendingOperations.set(signature, operationId);
    const result = await reprocessLogFn({ logId, operationId, ...options });
    pendingOperations.delete(signature);
    return result.data;
  } catch (error: unknown) {
    console.error("Reprocess Log Error:", error);
    throw new Error("再処理に失敗しました。処理ログを確認してください");
  }
}

/**
 * ログの強制中断を実行
 * 
 * スタックした処理中タスクのステータスを手動で「中断」に変更する。
 * 実際のCloud Functionsプロセスを止めるわけではなく、ステータスを更新するのみ。
 */
export async function abortLog(logId: string, reason?: string): Promise<{ success: boolean; message: string }> {
  const functions = getFunctionsInstance();
  const abortLogFn = httpsCallable<{ logId: string; reason?: string }, { success: boolean; message: string }>(
    functions, 
    "abortLog"
  );
  
  try {
    const result = await abortLogFn({ logId, reason });
    return result.data;
  } catch (error: unknown) {
    console.error("Abort Log Error:", error);
    throw new Error("処理の中断に失敗しました");
  }
}

/**
 * 指定されたログIDが存在するページ番号を計算する
 * 
 * ログは receivedAt の降順で表示されるため、
 * 指定されたログより新しいログの件数をカウントしてページ番号を算出する。
 * 
 * @param logId 対象のログID
 * @param pageSize ページサイズ（デフォルト: DEFAULT_PAGE_SIZE）
 * @returns ページ番号（1始まり）、ログが見つからない場合は1を返す
 */
export async function calculateLogPage(
  logId: string,
  pageSize: number = DEFAULT_PAGE_SIZE
): Promise<{ page: number; total: number }> {
  const headers = await getAuthHeaders();
  const params = new URLSearchParams();
  params.append("logId", logId);
  params.append("pageSize", pageSize.toString());

  const url = `${API_BASE_URL}/position?${params}`;
  const response = await fetch(url, { headers });
  
  // APIが404を返した場合（ログが見つからない場合）はページ1を返す
  if (response.status === 404) {
    return { page: 1, total: 0 };
  }
  
  const data = await handleResponse<{ page: number; total: number }>(response);
  return data;
}
